// DESIGN V2 — Library › Analytics tab.
//
// Everything is computed from the rows the Tracks tab is showing right now
// (source picker + filters + text filter), so the two tabs always describe the
// same set. Layout: a stat strip, then two-up cards (Year | Genre, Tempo | Keys,
// Top artists | Tag coverage) that stack below ~1100px. Styles live in
// styles/design-v2/_library-analytics.scss.
import React from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell,
} from 'recharts';
import { formatKeyDisplay, camelotColor, parseKeyToCamelot } from '../../utils/harmonic';

const CHART_COLORS = [1, 2, 3, 4, 5, 6, 7, 8].map((i) => `var(--v2-chart-${i})`);
const TOP_GENRES = 5;

const parseYear = (v) => {
    const m = String(v ?? '').match(/\d{4}/);
    const y = m ? parseInt(m[0], 10) : NaN;
    return y >= 1900 && y <= 2100 ? y : NaN;
};
const genreOf = (t) => t?.styles?.[0] || t?.genres?.[0] || t?.genre || '';
const median = (xs) => {
    if (!xs.length) return null;
    const s = [...xs].sort((a, b) => a - b);
    const mid = s.length >> 1;
    return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
};
const pct = (n, d) => (d ? Math.round((n / d) * 100) : 0);
const plural = (n, word) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

// Artist credits: the KB split ("A & B" duo vs collab) when known, else commas.
const splitArtists = (raw, partsMap) => {
    if (!raw) return [];
    const parts = partsMap ? partsMap.get(raw) : null;
    if (parts && parts.length) return parts;
    return raw.split(/\s*,\s*/).filter(Boolean);
};

export function computeLibraryStats(files, artistPartsMap) {
    const tracks = [];
    for (const f of files || []) {
        const t = f?.result?.track;
        if (t) tracks.push(t);
    }
    const n = tracks.length;
    const bpms = [], years = [];
    const genres = new Map(), keys = new Map(), artists = new Map();
    let withGenre = 0, withKey = 0, withEnergy = 0, withLabel = 0;

    for (const t of tracks) {
        const b = parseFloat(t.bpm);
        if (b > 0 && isFinite(b)) bpms.push(b);
        const y = parseYear(t.releaseDate);
        if (!isNaN(y)) years.push(y);
        const g = genreOf(t).trim();
        if (g) { withGenre++; genres.set(g, (genres.get(g) || 0) + 1); }
        const cam = t.key ? parseKeyToCamelot(t.key) : null;
        if (t.key) withKey++;
        if (cam) {
            const e = keys.get(cam) || { cam, raw: t.key, count: 0 };
            e.count++;
            keys.set(cam, e);
        }
        if (t.energy != null && !isNaN(parseFloat(t.energy))) withEnergy++;
        if (t.label) withLabel++;
        for (const a of splitArtists(t.artist, artistPartsMap)) {
            artists.set(a, (artists.get(a) || 0) + 1);
        }
    }

    // Year: one bar per year when the span is short enough to read as a
    // timeline (gaps kept as empty years), decades otherwise.
    let yearBars = [];
    if (years.length) {
        const lo = Math.min(...years), hi = Math.max(...years);
        const counts = new Map();
        if (hi - lo <= 15) {
            years.forEach((y) => counts.set(y, (counts.get(y) || 0) + 1));
            for (let y = lo; y <= hi; y++) yearBars.push({ label: String(y), count: counts.get(y) || 0, range: String(y) });
        } else {
            years.forEach((y) => { const d = Math.floor(y / 10) * 10; counts.set(d, (counts.get(d) || 0) + 1); });
            for (let d = Math.floor(lo / 10) * 10; d <= hi; d += 10) {
                yearBars.push({ label: `${d}s`, count: counts.get(d) || 0, range: `${d}–${d + 9}` });
            }
        }
    }

    // Genre: top N, the rest (and any genre literally tagged "Other") folded
    // into ONE "Other" slice.
    const genreSorted = [...genres.entries()].sort((a, b) => b[1] - a[1]);
    const named = genreSorted.filter(([g]) => g.toLowerCase() !== 'other');
    const genreSlices = named.slice(0, TOP_GENRES).map(([genre, value]) => ({ genre, value }));
    const otherCount = withGenre - genreSlices.reduce((s, g) => s + g.value, 0);
    if (otherCount > 0) genreSlices.push({ genre: 'Other', value: otherCount, isOther: true });

    // Tempo: 2-BPM bins for a tight library, 5-BPM bins for a wide one.
    let bpmBars = [];
    if (bpms.length) {
        const lo = Math.floor(Math.min(...bpms)), hi = Math.ceil(Math.max(...bpms));
        const step = hi - lo <= 30 ? 2 : 5;
        const start = Math.floor(lo / step) * step;
        const bins = new Map();
        bpms.forEach((b) => { const k = Math.floor(b / step) * step; bins.set(k, (bins.get(k) || 0) + 1); });
        for (let k = start; k <= hi; k += step) {
            bpmBars.push({ label: String(k), count: bins.get(k) || 0, range: `${k}–${k + step - 1} BPM` });
        }
    }

    const keyRows = [...keys.values()].sort((a, b) => b.count - a.count);
    const artistRows = [...artists.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6)
        .map(([name, count]) => ({ name, count }));

    return {
        n,
        artistCount: artists.size,
        bpmMedian: median(bpms),
        bpmLo: bpms.length ? Math.round(Math.min(...bpms)) : null,
        bpmHi: bpms.length ? Math.round(Math.max(...bpms)) : null,
        yearBars, genreSlices, withGenre, bpmBars, keyRows, artistRows,
        coverage: [
            { field: 'BPM', have: bpms.length },
            { field: 'Key', have: withKey },
            { field: 'Genre', have: withGenre },
            { field: 'Year', have: years.length },
            { field: 'Label', have: withLabel },
            { field: 'Energy', have: withEnergy },
        ],
    };
}

const ChartTooltip = ({ active, payload, unit = 'track' }) => {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0].payload;
    const count = p.count ?? p.value;
    return (
        <div className="v2-la-tip">
            <b>{p.range || p.genre || p.label}</b>
            <span>{plural(count, unit)}</span>
        </div>
    );
};

const Card = ({ title, sub, className = '', children }) => (
    <section className={`v2-la-card ${className}`}>
        <header>
            <h4>{title}</h4>
            {sub && <span className="v2-la-sub">{sub}</span>}
        </header>
        {children}
    </section>
);

const Empty = ({ children }) => <div className="v2-la-empty">{children}</div>;

const BarsChart = ({ data, ariaLabel }) => (
    <div className="v2-la-chart" role="img" aria-label={ariaLabel}>
        <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 8, right: 4, left: 0, bottom: 0 }} barCategoryGap="18%">
                <defs>
                    <linearGradient id="v2LaBar" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--color-accent)" stopOpacity={1} />
                        <stop offset="100%" stopColor="var(--color-accent)" stopOpacity={0.55} />
                    </linearGradient>
                </defs>
                <CartesianGrid vertical={false} strokeDasharray="0" />
                <XAxis dataKey="label" tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={8} />
                <YAxis tickLine={false} axisLine={false} allowDecimals={false} width={32} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: 'var(--v2-surface-2)', radius: 6 }} isAnimationActive={false} />
                <Bar dataKey="count" fill="url(#v2LaBar)" radius={[5, 5, 0, 0]} maxBarSize={44} animationDuration={500} />
            </BarChart>
        </ResponsiveContainer>
    </div>
);

const GenreDonut = ({ slices, total }) => {
    const [active, setActive] = React.useState(null);
    const focus = active != null ? slices[active] : slices[0];
    return (
        <div className="v2-la-donut-wrap">
            <div className="v2-la-donut" role="img" aria-label="Genre distribution">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={slices} dataKey="value" nameKey="genre"
                            innerRadius="68%" outerRadius="100%" paddingAngle={slices.length > 1 ? 2 : 0}
                            stroke="none" startAngle={90} endAngle={-270} animationDuration={500}
                            onMouseEnter={(_, i) => setActive(i)} onMouseLeave={() => setActive(null)}
                        >
                            {slices.map((s, i) => (
                                <Cell key={s.genre} fill={s.isOther ? 'var(--v2-hairline-strong)' : CHART_COLORS[i % CHART_COLORS.length]}
                                    opacity={active == null || active === i ? 1 : 0.35} />
                            ))}
                        </Pie>
                    </PieChart>
                </ResponsiveContainer>
                {focus && (
                    <div className="v2-la-donut-center">
                        <b className="v2-num">{pct(focus.value, total)}%</b>
                        <span title={focus.genre}>{focus.genre}</span>
                    </div>
                )}
            </div>
            <ul className="v2-la-legend">
                {slices.map((s, i) => (
                    <li key={s.genre} className={active === i ? 'on' : ''}
                        onMouseEnter={() => setActive(i)} onMouseLeave={() => setActive(null)}>
                        <i style={{ background: s.isOther ? 'var(--v2-hairline-strong)' : CHART_COLORS[i % CHART_COLORS.length] }} />
                        <span className="name" title={s.genre}>{s.genre}</span>
                        <span className="v2-num v2-faint">{s.value}</span>
                        <span className="v2-num pct">{pct(s.value, total)}%</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

const RankList = ({ rows, max, colorOf, labelOf }) => (
    <ol className="v2-la-rank">
        {rows.map((r, i) => (
            <li key={labelOf(r) + i}>
                <span className="v2-num v2-faint idx">{i + 1}</span>
                <span className="name" title={labelOf(r)}>
                    {colorOf && <i className="dot" style={{ background: colorOf(r) || 'var(--color-text-tertiary)' }} />}
                    {labelOf(r)}
                </span>
                <span className="meter"><span style={{ width: `${Math.max(4, (r.count / max) * 100)}%`, background: colorOf ? (colorOf(r) || 'var(--color-accent)') : undefined }} /></span>
                <span className="v2-num count">{r.count}</span>
            </li>
        ))}
    </ol>
);

export default function LibraryAnalyticsV2({ files, keyNotation = 'musical', artistPartsMap }) {
    const s = React.useMemo(() => computeLibraryStats(files, artistPartsMap), [files, artistPartsMap]);

    if (!s.n) {
        return (
            <div className="v2-la">
                <Empty>No tracks in this view. Clear the filters or pick another source to see its analytics.</Empty>
            </div>
        );
    }

    const keyLabel = (r) => formatKeyDisplay(r.raw, keyNotation) || r.cam;
    const topKey = s.keyRows[0];
    const topGenre = s.genreSlices.find((g) => !g.isOther);
    const keyRowsShown = s.keyRows.slice(0, 6);
    const keysMore = s.keyRows.length - keyRowsShown.length;

    return (
        <div className="v2-la">
            <div className="v2-la-stats">
                <div className="v2-la-stat">
                    <span className="lbl">Tracks</span>
                    <b className="v2-num">{s.n.toLocaleString()}</b>
                    <span className="v2-faint">{s.artistCount ? `by ${plural(s.artistCount, 'artist')}` : 'in this view'}</span>
                </div>
                <div className="v2-la-stat">
                    <span className="lbl">Tempo</span>
                    <b className="v2-num">{s.bpmMedian != null ? Math.round(s.bpmMedian) : '—'}<small> BPM</small></b>
                    <span className="v2-faint v2-num">{s.bpmLo != null ? (s.bpmLo === s.bpmHi ? 'median' : `median · ${s.bpmLo}–${s.bpmHi}`) : 'not analyzed'}</span>
                </div>
                <div className="v2-la-stat">
                    <span className="lbl">Top key</span>
                    <b>{topKey ? <><i className="dot" style={{ background: camelotColor(topKey.raw) || 'var(--color-text-tertiary)' }} />{keyLabel(topKey)}</> : '—'}</b>
                    <span className="v2-faint">{topKey ? plural(topKey.count, 'track') : 'not analyzed'}</span>
                </div>
                <div className="v2-la-stat">
                    <span className="lbl">Top genre</span>
                    <b className="ellipsis" title={topGenre?.genre}>{topGenre ? topGenre.genre : '—'}</b>
                    <span className="v2-faint">{topGenre ? `${pct(topGenre.value, s.withGenre)}% of tagged tracks` : 'no genre tags'}</span>
                </div>
            </div>

            <div className="v2-la-grid">
                <Card title="Release year" sub={s.yearBars.length ? plural(s.yearBars.reduce((a, b) => a + b.count, 0), 'dated track') : null}>
                    {s.yearBars.length ? <BarsChart data={s.yearBars} ariaLabel="Tracks per release year" /> : <Empty>No release dates yet.</Empty>}
                </Card>
                <Card title="Genre" sub={s.withGenre ? plural(s.withGenre, 'tagged track') : null}>
                    {s.genreSlices.length ? <GenreDonut slices={s.genreSlices} total={s.withGenre} /> : <Empty>No genre tags yet.</Empty>}
                </Card>
                <Card title="Tempo" sub={s.bpmBars.length ? 'tracks per BPM range' : null}>
                    {s.bpmBars.length ? <BarsChart data={s.bpmBars} ariaLabel="Tracks per BPM range" /> : <Empty>No BPM analyzed yet.</Empty>}
                </Card>
                <Card title="Keys" sub={s.keyRows.length ? plural(s.keyRows.length, 'key') : null}>
                    {s.keyRows.length ? (
                        <>
                            <RankList rows={keyRowsShown} max={s.keyRows[0].count} labelOf={keyLabel} colorOf={(r) => camelotColor(r.raw)} />
                            {keysMore > 0 && <div className="v2-la-more v2-faint">+ {plural(keysMore, 'more key')}</div>}
                        </>
                    ) : <Empty>No keys analyzed yet.</Empty>}
                </Card>
                <Card title="Top artists">
                    {s.artistRows.length ? <RankList rows={s.artistRows} max={s.artistRows[0].count} labelOf={(r) => r.name} /> : <Empty>No artist tags yet.</Empty>}
                </Card>
                <Card title="Tag coverage" sub="tracks with each value">
                    <ul className="v2-la-coverage">
                        {s.coverage.map((c) => {
                            const p = pct(c.have, s.n);
                            return (
                                <li key={c.field} className={p === 100 ? 'full' : ''}>
                                    <span className="name">{c.field}</span>
                                    <span className="meter"><span style={{ width: `${p}%` }} /></span>
                                    <span className="v2-num count">{c.have}/{s.n}</span>
                                </li>
                            );
                        })}
                    </ul>
                </Card>
            </div>
        </div>
    );
}
